import React from 'react';
import {
  Alert,
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { WarehouseSelect } from '../ui/WarehouseSelect';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CloudUpload as PushIcon,
  MenuBook as DocsIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import PolicyAuxDialogs from './PolicyAuxDialogs';
import PolicyDetailsDialog from './PolicyDetailsDialog';
import PolicyStatusBadge from './PolicyStatusBadge';
import PackPolicyCreateDialog from './PackPolicyCreateDialog';
import { PACK_BY_KEY, type PackKey, type PolicySubject } from '../../domain/policies/policyPacks';
import type { Policy, PolicyCreateData } from '../../services/policiesApi';
import type { Camera } from '../../services/camerasApi';
import type { Line } from '../../services/linesApi';
import type { Zone } from '../../services/zonesApi';
import { CONFIG_SCOPE_LABELS, CONFIG_STATE_LABELS } from '../../utils/configVocabulary';
import type { StartMeasuringPresentation } from '../../domain/safetyCases/startMeasuring';

type PoliciesPageContentProps = {
  PoliciesIntroSectionComponent: React.ComponentType<any>;
  PoliciesPackSectionsComponent: React.ComponentType<any>;
  PoliciesEditorBodyComponent: React.ComponentType<any>;
  qReturnTo: string;
  canGoBack: boolean;
  handleBackFromPolicies: () => void;
  canWritePolicies: boolean;
  canDeployPolicies: boolean;
  openCreatePolicy: () => void;
  openCreatePolicyPack: () => void;
  handleDeployAll: () => void;
  setPageMenuAnchor: (value: HTMLElement | null) => void;
  pageMenuAnchor: HTMLElement | null;
  packSummaries: any[];
  packFilter: any;
  setPackFilter: (value: any) => void;
  policySummary: any;
  qPack: string | null;
  qType: string;
  qCameraId: string;
  qZoneId: string;
  qLineId: string;
  clearFilters: () => void;
  getPolicyTypeLabel: (value: any, zonesById?: any) => string;
  siteComplexity: any;
  canReadPolicies: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: any) => void;
  policiesLoaded: boolean;
  policyRows: any[];
  loading: boolean;
  groupedPolicyViews: any;
  filteredPolicyRows: any[];
  selectedPolicyView: any;
  setPolicyDetailsId: (value: string) => void;
  selectedPolicyPresented: any;
  zonesById: any;
  canaryGroup: { id: string; name: string } | null;
  scopeCanary: number | null;
  scopeFull: number | null;
  navigate: (value: string) => void;
  handleOpenDialog: (policy?: any) => void;
  openOverrideDialog: (policy: any) => void;
  handleActivate: (policy: any) => void;
  handleDeactivate: (policy: any) => void;
  handleDeploy: (policy: any, groupId?: string) => void;
  handleDelete: (policy: any) => void;
  openMeasureForPolicy: (policy: Policy) => void;
  measurement?: StartMeasuringPresentation | null;
  measurementBusy?: boolean;
  measurementError?: string | null;
  onStartMeasuring?: () => void;
  onSaveForLater?: () => void;
  fetchPolicies: () => void;
  fetchZones: () => void;
  fetchCameras: () => void;
  devicesWithPolicies: Array<{ deviceId: string; count: number }>;
  setDeleteAllDeviceOpen: (value: boolean) => void;
  policyMenuAnchor: HTMLElement | null;
  setPolicyMenuAnchor: (value: HTMLElement | null) => void;
  policyMenuRow: any;
  policyMenuRowId: string;
  setPolicyMenuRowId: (value: string) => void;
  dialogOpen: boolean;
  handleCloseDialog: () => void;
  editingPolicy: any;
  editorStep: number;
  setEditorStep: React.Dispatch<React.SetStateAction<number>>;
  selectedPolicyPack: PackKey | null;
  selectionReady: boolean;
  scopeReady: boolean;
  saving: boolean;
  canSavePolicy: boolean;
  primarySaveLabel: string;
  handleSave: () => void;
  selectedPolicyAlias: string;
  setSelectedPolicyAlias: (value: string) => void;
  setSelectedPolicyPack: (value: any) => void;
  applyPolicyAliasSelection: (value: string) => void;
  selectedPolicyOption: any;
  selectedPolicySubject: PolicySubject;
  formData: Partial<PolicyCreateData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<PolicyCreateData>>>;
  selectedCameraId: string;
  setSelectedCameraId: React.Dispatch<React.SetStateAction<string>>;
  selectedCameraProfileId: '' | 'A' | 'B' | 'C' | 'D';
  selectedEdgeDeviceId: string;
  setSelectedEdgeDeviceId: React.Dispatch<React.SetStateAction<string>>;
  cameras: Camera[];
  selectedZoneIds: string[];
  setSelectedZoneIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLineId: string;
  setSelectedLineId: React.Dispatch<React.SetStateAction<string>>;
  lineDirection: 'both' | 'a_to_b' | 'b_to_a';
  setLineDirection: React.Dispatch<React.SetStateAction<'both' | 'a_to_b' | 'b_to_a'>>;
  availableZones: Zone[];
  availableLines: Line[];
  openZonePreview: (zone: Zone) => void;
  scopeRequirements: string[];
  editorTab: 'scope' | 'hours' | 'actions';
  setEditorTab: React.Dispatch<React.SetStateAction<'scope' | 'hours' | 'actions'>>;
  weekGrid: Record<string, boolean[]>;
  setWeekGrid: React.Dispatch<React.SetStateAction<Record<string, boolean[]>>>;
  initDefaultGrid: () => Record<string, boolean[]>;
  gridToWindows: (value: Record<string, boolean[]>) => Record<string, Array<{ start: string; end: string }>>;
  mode: string;
  clockStatusByDevice: Record<string, boolean | null>;
  deriveDeviceIdsForPolicy: (policy: Partial<Policy>, opts?: any) => string[];
  setSnackbar: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'warning';
      autoHideDuration?: number;
    }>
  >;
  selectedPackGroup: (typeof PACK_BY_KEY)[PackKey] | null;
  allowsWholeViewScope: boolean;
  postCreateAction: 'draft' | 'activate' | 'activate_deploy';
  setPostCreateAction: React.Dispatch<React.SetStateAction<'draft' | 'activate' | 'activate_deploy'>>;
  sitePolicies?: Policy[];
  overrideOpen: boolean;
  setOverrideOpen: (value: boolean) => void;
  overridePolicy: any;
  overrideDisable: boolean;
  setOverrideDisable: (value: boolean) => void;
  overrideSeverity: any;
  setOverrideSeverity: (value: any) => void;
  overrideCooldownSeconds: string;
  setOverrideCooldownSeconds: (value: string) => void;
  overrideDurationMinutes: number;
  setOverrideDurationMinutes: (value: number) => void;
  overrideReason: string;
  setOverrideReason: (value: string) => void;
  overrideSaving: boolean;
  severityOptions: any[];
  submitOverride: () => void;
  deleteAllDeviceOpen: boolean;
  setDeleteAllDeviceOpenLocal: (value: boolean) => void;
  deleteAllDeviceSaving: boolean;
  deleteAllDeviceId: string;
  setDeleteAllDeviceId: (value: string) => void;
  handleDeleteAllPoliciesForDevice: () => void;
  zonePreviewOpen: boolean;
  closeZonePreviewDialog: () => void;
  zonePreviewZone: any;
  zonePreviewImg: string | null;
  zonePreviewLoading: boolean;
  zonePreviewError: string | null;
  zoneOverlay: any;
  deployLintOpen: boolean;
  setDeployLintOpen: (value: boolean) => void;
  deployLintTitle: string;
  deployLintErrors: string[];
  lintFixHref: (value: any) => string | null;
  openLintFix: (href: string) => void;
  clockGateOpen: boolean;
  setClockGateOpen: (value: boolean) => void;
  clockGatePolicy: any;
  clockGateDevices: string[];
  openClockPreflight: () => void;
  snackbar: any;
  closeSnackbar: () => void;
};

const PoliciesPageContent: React.FC<PoliciesPageContentProps> = ({
  PoliciesIntroSectionComponent,
  PoliciesPackSectionsComponent,
  PoliciesEditorBodyComponent,
  qReturnTo,
  canGoBack,
  handleBackFromPolicies,
  canWritePolicies,
  canDeployPolicies,
  openCreatePolicy,
  openCreatePolicyPack,
  handleDeployAll,
  setPageMenuAnchor,
  pageMenuAnchor,
  packSummaries,
  packFilter,
  setPackFilter,
  policySummary,
  qPack,
  qType,
  qCameraId,
  qZoneId,
  qLineId,
  clearFilters,
  getPolicyTypeLabel,
  siteComplexity,
  canReadPolicies,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  policiesLoaded,
  policyRows,
  loading,
  groupedPolicyViews,
  filteredPolicyRows,
  selectedPolicyView,
  setPolicyDetailsId,
  selectedPolicyPresented,
  zonesById,
  canaryGroup,
  scopeCanary,
  scopeFull,
  navigate,
  handleOpenDialog,
  openOverrideDialog,
  handleActivate,
  handleDeactivate,
  handleDeploy,
  handleDelete,
  openMeasureForPolicy,
  measurement,
  measurementBusy,
  measurementError,
  onStartMeasuring,
  onSaveForLater,
  fetchPolicies,
  fetchZones,
  fetchCameras,
  devicesWithPolicies,
  setDeleteAllDeviceOpen,
  policyMenuAnchor,
  setPolicyMenuAnchor,
  policyMenuRow,
  setPolicyMenuRowId,
  dialogOpen,
  handleCloseDialog,
  editingPolicy,
  editorStep,
  setEditorStep,
  selectedPolicyPack,
  selectionReady,
  scopeReady,
  saving,
  canSavePolicy,
  primarySaveLabel,
  handleSave,
  selectedPolicyAlias,
  setSelectedPolicyAlias,
  setSelectedPolicyPack,
  applyPolicyAliasSelection,
  selectedPolicyOption,
  selectedPolicySubject,
  formData,
  setFormData,
  selectedCameraId,
  setSelectedCameraId,
  selectedCameraProfileId,
  selectedEdgeDeviceId,
  setSelectedEdgeDeviceId,
  cameras,
  selectedZoneIds,
  setSelectedZoneIds,
  selectedLineId,
  setSelectedLineId,
  lineDirection,
  setLineDirection,
  availableZones,
  availableLines,
  openZonePreview,
  scopeRequirements,
  editorTab,
  setEditorTab,
  weekGrid,
  setWeekGrid,
  initDefaultGrid,
  gridToWindows,
  mode,
  clockStatusByDevice,
  deriveDeviceIdsForPolicy,
  setSnackbar,
  selectedPackGroup,
  allowsWholeViewScope,
  postCreateAction,
  setPostCreateAction,
  sitePolicies = [],
  overrideOpen,
  setOverrideOpen,
  overridePolicy,
  overrideDisable,
  setOverrideDisable,
  overrideSeverity,
  setOverrideSeverity,
  overrideCooldownSeconds,
  setOverrideCooldownSeconds,
  overrideDurationMinutes,
  setOverrideDurationMinutes,
  overrideReason,
  setOverrideReason,
  overrideSaving,
  severityOptions,
  submitOverride,
  deleteAllDeviceOpen,
  setDeleteAllDeviceOpenLocal,
  deleteAllDeviceSaving,
  deleteAllDeviceId,
  setDeleteAllDeviceId,
  handleDeleteAllPoliciesForDevice,
  zonePreviewOpen,
  closeZonePreviewDialog,
  zonePreviewZone,
  zonePreviewImg,
  zonePreviewLoading,
  zonePreviewError,
  zoneOverlay,
  deployLintOpen,
  setDeployLintOpen,
  deployLintTitle,
  deployLintErrors,
  lintFixHref,
  openLintFix,
  clockGateOpen,
  setClockGateOpen,
  clockGatePolicy,
  clockGateDevices,
  openClockPreflight,
  snackbar,
  closeSnackbar,
}) => (
  <Box sx={{ p: 3 }}>
    <PoliciesIntroSectionComponent
      qReturnTo={qReturnTo}
      canGoBack={canGoBack}
      onBack={handleBackFromPolicies}
      canWritePolicies={canWritePolicies}
      canDeployPolicies={canDeployPolicies}
      onOpenAddPolicy={openCreatePolicy}
      onOpenAddPolicyPack={openCreatePolicyPack}
      onDeployAll={handleDeployAll}
      onOpenMenu={(event: React.MouseEvent<HTMLElement>) => setPageMenuAnchor(event.currentTarget)}
      packSummaries={packSummaries}
      selectedPack={packFilter}
      onSelectPack={setPackFilter}
      summary={policySummary}
    />

    {(qPack || qType || qCameraId || qZoneId || qLineId) && (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle2">Filters active</Typography>
            <Typography variant="body2" color="text.secondary">
              {qPack ? `Pack: ${PACK_BY_KEY[qPack as keyof typeof PACK_BY_KEY].label} ` : ''}
              {qType ? `Policy type: ${getPolicyTypeLabel(qType)} ` : ''}
              {qCameraId ? `${CONFIG_SCOPE_LABELS.cameraStream}: ${qCameraId} ` : ''}
              {qZoneId ? `${CONFIG_SCOPE_LABELS.zone}: ${qZoneId} ` : ''}
              {qLineId ? `${CONFIG_SCOPE_LABELS.line}: ${qLineId} ` : ''}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={clearFilters}>
            Clear filters
          </Button>
        </Box>
      </Alert>
    )}

    <Alert severity="info" sx={{ mb: 2 }}>
      Policies define when incidents are generated. {CONFIG_STATE_LABELS.live} policies should be assigned to {CONFIG_SCOPE_LABELS.zone.toLowerCase()}s when required and
      {CONFIG_STATE_LABELS.deployed.toLowerCase()} before they can affect edge behavior.
    </Alert>
    <Alert severity="info" sx={{ mb: 2 }}>
      Status shows configuration state. Deployment shows whether live incident behavior matches the latest saved policy.{' '}
      {CONFIG_STATE_LABELS.needsAction} means Incident Review may still reflect older edge behavior until deployment finishes.
    </Alert>

    {(policySummary.needsZone > 0 || policySummary.inactive > 0 || policySummary.pendingDeploy > 0) && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {[
          policySummary.needsZone > 0
            ? `${policySummary.needsZone} polic${policySummary.needsZone === 1 ? 'y needs' : 'ies need'} ${CONFIG_SCOPE_LABELS.zone.toLowerCase()} scope`
            : null,
          policySummary.inactive > 0
            ? `${policySummary.inactive} polic${policySummary.inactive === 1 ? 'y is' : 'ies are'} ${CONFIG_STATE_LABELS.draft.toLowerCase()}`
            : null,
          policySummary.pendingDeploy > 0
            ? `${policySummary.pendingDeploy} polic${policySummary.pendingDeploy === 1 ? 'y needs' : 'ies need'} deployment action`
            : null,
        ]
          .filter(Boolean)
          .join('. ')}
        .
      </Alert>
    )}

    {canReadPolicies && !canWritePolicies && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Read-only policy access. Viewing is available, but create, edit, delete, and deploy actions are disabled.
      </Alert>
    )}

    {!!siteComplexity?.pilot_mode && (siteComplexity.warnings || []).length > 0 && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Pilot-mode scoring flagged {(siteComplexity.warnings || []).length} configuration signal
        {(siteComplexity.warnings || []).length === 1 ? '' : 's'} for review before the next deploy.
      </Alert>
    )}

    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="Search policies"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />
        <WarehouseSelect
          label="Status"
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as typeof statusFilter)}
          minWidth={180}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: CONFIG_STATE_LABELS.live },
            { value: 'inactive', label: CONFIG_STATE_LABELS.draft },
            { value: 'draft', label: CONFIG_STATE_LABELS.draft },
            { value: 'invalid', label: CONFIG_STATE_LABELS.blocked },
            {
              value: 'needs_zone',
              label: `${CONFIG_STATE_LABELS.needsAction}: assign ${CONFIG_SCOPE_LABELS.zone.toLowerCase()}`,
            },
            { value: 'pending_deploy', label: `${CONFIG_STATE_LABELS.needsAction}: deploy` },
          ]}
        />
        {packFilter !== 'all' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pack filter
            </Typography>
            <PolicyStatusBadge label={PACK_BY_KEY[packFilter as keyof typeof PACK_BY_KEY]?.label || packFilter} kind="pack" />
          </Box>
        ) : null}
        <Button variant="text" onClick={clearFilters}>
          Clear filters
        </Button>
      </Box>
    </Paper>

    {!policiesLoaded && policyRows.length === 0 ? (
      <Box>
        {[0, 1, 2].map((i) => (
          <Paper key={i} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" height={28} />
                <Skeleton variant="text" width="60%" />
              </Box>
              <Skeleton variant="rounded" width={70} height={24} />
            </Box>
          </Paper>
        ))}
      </Box>
    ) : policySummary.total === 0 ? (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
        <PolicyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No policies configured
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Policies are the step after devices, cameras, and zones are ready. Stage at least one policy here, then run
          Preflight and deploy before expecting incident review or proof artifacts.
        </Typography>
        <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePolicy} disabled={!canWritePolicies}>
            Add Policy
          </Button>
          <Button variant="outlined" onClick={openCreatePolicyPack} disabled={!canWritePolicies}>
            Add Policy Pack
          </Button>
          <Button variant="text" component="a" href="/setup-rollout">
            View setup journey
          </Button>
        </Box>
      </Paper>
    ) : (
      <>
        {groupedPolicyViews.attention.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {groupedPolicyViews.attention.length} polic
            {groupedPolicyViews.attention.length === 1 ? 'y needs' : 'ies need'} attention. Review target binding,
            zone integrity, or deployment failures first.
          </Alert>
        )}

        {filteredPolicyRows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              {packFilter !== 'all' ? 'No policies in this pack' : 'No policies match the current filters'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mb: 2 }}>
              {packFilter !== 'all'
                ? 'This safety pack does not contain any policies yet.'
                : 'Clear one or more filters to return to the full policy list.'}
            </Typography>
            <Button
              variant={packFilter !== 'all' ? 'contained' : 'outlined'}
              onClick={packFilter !== 'all' ? openCreatePolicy : clearFilters}
              disabled={packFilter !== 'all' && !canWritePolicies}
            >
              {packFilter !== 'all' ? 'Add Policy to Pack' : 'Clear filters'}
            </Button>
          </Paper>
        ) : (
          <PoliciesPackSectionsComponent
            rows={filteredPolicyRows}
            loading={loading && !policiesLoaded}
            selectedPolicyId={selectedPolicyView?.policy.id || null}
            onSelectPolicy={(row: any) => setPolicyDetailsId(row.id)}
            onOpenActions={(event: React.MouseEvent<HTMLElement>, row: any) => {
              setPolicyMenuAnchor(event.currentTarget);
              setPolicyMenuRowId(row.id);
            }}
          />
        )}

        <PolicyDetailsDialog
          open={Boolean(selectedPolicyView)}
          view={selectedPolicyView}
          presented={selectedPolicyPresented}
          policyTypeLabel={selectedPolicyView ? getPolicyTypeLabel(selectedPolicyView.policy, zonesById) : getPolicyTypeLabel('')}
          canWritePolicies={canWritePolicies}
          canDeployPolicies={canDeployPolicies}
          canaryGroup={canaryGroup}
          scopeCanary={scopeCanary}
          scopeFull={scopeFull}
          onClose={() => setPolicyDetailsId('')}
          onEdit={() => selectedPolicyView && handleOpenDialog(selectedPolicyView.policy)}
          onOverride={() => selectedPolicyView && openOverrideDialog(selectedPolicyView.policy)}
          onActivate={() => selectedPolicyView && handleActivate(selectedPolicyView.policy)}
          onDeactivate={() => selectedPolicyView && handleDeactivate(selectedPolicyView.policy)}
          onDeploy={(groupId?: string) => selectedPolicyView && handleDeploy(selectedPolicyView.policy, groupId)}
          onDelete={() => selectedPolicyView && handleDelete(selectedPolicyView.policy)}
          onMeasure={() => selectedPolicyView && openMeasureForPolicy(selectedPolicyView.policy)}
          onOpenDeployments={() => navigate('/deployments')}
          measurement={measurement}
          measurementBusy={measurementBusy}
          measurementError={measurementError}
          onStartMeasuring={onStartMeasuring}
          onSaveForLater={onSaveForLater}
        />
      </>
    )}

    <Menu
      anchorEl={pageMenuAnchor}
      open={Boolean(pageMenuAnchor)}
      onClose={() => setPageMenuAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={() => {
          setPageMenuAnchor(null);
          void Promise.all([fetchPolicies(), fetchZones(), fetchCameras()]);
        }}
      >
        <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
        Refresh now
      </MenuItem>
      {devicesWithPolicies.length > 0 ? (
        <MenuItem
          onClick={() => {
            setPageMenuAnchor(null);
            setDeleteAllDeviceOpen(true);
          }}
          disabled={!canWritePolicies}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete all on device…
        </MenuItem>
      ) : null}
      {(qReturnTo === 'setup' || window.history.length > 1) && (
        <MenuItem
          onClick={() => {
            setPageMenuAnchor(null);
            handleBackFromPolicies();
          }}
        >
          Back
        </MenuItem>
      )}
      <MenuItem
        component="a"
        href="/docs/user-guide/configuration/policies/"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setPageMenuAnchor(null)}
      >
        <DocsIcon fontSize="small" sx={{ mr: 1 }} />
        Open documentation
      </MenuItem>
    </Menu>

    <Menu
      anchorEl={policyMenuAnchor}
      open={Boolean(policyMenuAnchor)}
      onClose={() => {
        setPolicyMenuAnchor(null);
        setPolicyMenuRowId('');
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={() => {
          if (policyMenuRow) setPolicyDetailsId(policyMenuRow.id);
          setPolicyMenuAnchor(null);
          setPolicyMenuRowId('');
        }}
        disabled={!policyMenuRow}
      >
        <ViewIcon fontSize="small" sx={{ mr: 1 }} />
        View details
      </MenuItem>
      <MenuItem
        onClick={() => {
          if (policyMenuRow) handleOpenDialog(policyMenuRow.policy);
          setPolicyMenuAnchor(null);
          setPolicyMenuRowId('');
        }}
        disabled={!policyMenuRow || !canWritePolicies}
      >
        <EditIcon fontSize="small" sx={{ mr: 1 }} />
        Edit policy
      </MenuItem>
      <MenuItem
        onClick={() => {
          if (policyMenuRow) void handleDeploy(policyMenuRow.policy);
          setPolicyMenuAnchor(null);
          setPolicyMenuRowId('');
        }}
        disabled={!policyMenuRow || !canDeployPolicies || policyMenuRow?.policy.status !== 'active' || policyMenuRow?.policy.enabled === false}
      >
        <PushIcon fontSize="small" sx={{ mr: 1 }} />
        Deploy policy
      </MenuItem>
      {policyMenuRow?.policy.status === 'active' ? (
        <MenuItem
          onClick={() => {
            if (policyMenuRow) void handleDeactivate(policyMenuRow.policy);
            setPolicyMenuAnchor(null);
            setPolicyMenuRowId('');
          }}
          disabled={!canWritePolicies}
        >
          Deactivate
        </MenuItem>
      ) : (
        <MenuItem
          onClick={() => {
            if (policyMenuRow) void handleActivate(policyMenuRow.policy);
            setPolicyMenuAnchor(null);
            setPolicyMenuRowId('');
          }}
          disabled={!canWritePolicies}
        >
          Activate
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          if (policyMenuRow) openOverrideDialog(policyMenuRow.policy);
          setPolicyMenuAnchor(null);
          setPolicyMenuRowId('');
        }}
        disabled={!policyMenuRow || !canWritePolicies}
      >
        Override
      </MenuItem>
      <MenuItem
        onClick={() => {
          navigate('/deployments');
          setPolicyMenuAnchor(null);
          setPolicyMenuRowId('');
        }}
        disabled={!policyMenuRow}
      >
        View deployments
      </MenuItem>
      <MenuItem
        onClick={() => {
          if (policyMenuRow) void handleDelete(policyMenuRow.policy);
          setPolicyMenuAnchor(null);
          setPolicyMenuRowId('');
        }}
        disabled={!policyMenuRow || !canWritePolicies}
        sx={{ color: 'error.main' }}
      >
        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
        Delete
      </MenuItem>
    </Menu>

    <PackPolicyCreateDialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      editingPolicy={editingPolicy}
      editorStep={editorStep}
      setEditorStep={setEditorStep}
      selectedPolicyPack={selectedPolicyPack}
      selectionReady={selectionReady}
      scopeReady={scopeReady}
      saving={saving}
      canSavePolicy={canSavePolicy}
      primarySaveLabel={primarySaveLabel}
      onSave={handleSave}
    >
      <PoliciesEditorBodyComponent
        editorStep={editorStep}
        selectedPolicyPack={selectedPolicyPack}
        selectedPolicyAlias={selectedPolicyAlias}
        setSelectedPolicyPack={setSelectedPolicyPack}
        setSelectedPolicyAlias={setSelectedPolicyAlias}
        setEditorStep={setEditorStep}
        applyPolicyAliasSelection={applyPolicyAliasSelection}
        selectedPolicyOption={selectedPolicyOption}
        selectedPolicySubject={selectedPolicySubject}
        formData={formData}
        setFormData={setFormData}
        selectedCameraId={selectedCameraId}
        selectedCameraProfileId={selectedCameraProfileId}
        setSelectedCameraId={setSelectedCameraId}
        selectedEdgeDeviceId={selectedEdgeDeviceId}
        setSelectedEdgeDeviceId={setSelectedEdgeDeviceId}
        cameras={cameras}
        selectedZoneIds={selectedZoneIds}
        setSelectedZoneIds={setSelectedZoneIds}
        selectedLineId={selectedLineId}
        setSelectedLineId={setSelectedLineId}
        lineDirection={lineDirection}
        setLineDirection={setLineDirection}
        availableZones={availableZones}
        availableLines={availableLines}
        openZonePreview={openZonePreview}
        scopeReady={scopeReady}
        scopeRequirements={scopeRequirements}
        editorTab={editorTab}
        setEditorTab={setEditorTab}
        weekGrid={weekGrid}
        setWeekGrid={setWeekGrid}
        initDefaultGrid={initDefaultGrid}
        gridToWindows={gridToWindows}
        mode={mode}
        saving={saving}
        editingPolicy={editingPolicy}
        clockStatusByDevice={clockStatusByDevice}
        deriveDeviceIdsForPolicy={deriveDeviceIdsForPolicy}
        navigate={navigate}
        setSnackbar={setSnackbar}
        selectedPackGroup={selectedPackGroup}
        allowsWholeViewScope={allowsWholeViewScope}
        postCreateAction={postCreateAction}
        setPostCreateAction={setPostCreateAction}
        sitePolicies={sitePolicies}
      />
    </PackPolicyCreateDialog>

    <PolicyAuxDialogs
      overrideOpen={overrideOpen}
      closeOverride={() => setOverrideOpen(false)}
      overridePolicy={overridePolicy}
      overrideDisable={overrideDisable}
      setOverrideDisable={setOverrideDisable}
      overrideSeverity={overrideSeverity}
      setOverrideSeverity={setOverrideSeverity}
      overrideCooldownSeconds={overrideCooldownSeconds}
      setOverrideCooldownSeconds={setOverrideCooldownSeconds}
      overrideDurationMinutes={overrideDurationMinutes}
      setOverrideDurationMinutes={setOverrideDurationMinutes}
      overrideReason={overrideReason}
      setOverrideReason={setOverrideReason}
      overrideSaving={overrideSaving}
      canWritePolicies={canWritePolicies}
      severityOptions={severityOptions as any}
      submitOverride={submitOverride}
      deleteAllDeviceOpen={deleteAllDeviceOpen}
      closeDeleteAllDevice={() => setDeleteAllDeviceOpenLocal(false)}
      deleteAllDeviceSaving={deleteAllDeviceSaving}
      deleteAllDeviceId={deleteAllDeviceId}
      setDeleteAllDeviceId={setDeleteAllDeviceId}
      devicesWithPolicies={devicesWithPolicies}
      handleDeleteAllPoliciesForDevice={handleDeleteAllPoliciesForDevice}
      zonePreviewOpen={zonePreviewOpen}
      closeZonePreview={closeZonePreviewDialog}
      zonePreviewZone={zonePreviewZone}
      zonePreviewImg={zonePreviewImg}
      zonePreviewLoading={zonePreviewLoading}
      zonePreviewError={zonePreviewError}
      zoneOverlay={zoneOverlay}
      deployLintOpen={deployLintOpen}
      closeDeployLint={() => setDeployLintOpen(false)}
      deployLintTitle={deployLintTitle}
      deployLintErrors={deployLintErrors}
      lintFixHref={lintFixHref}
      openLintFix={openLintFix}
      clockGateOpen={clockGateOpen}
      closeClockGate={() => setClockGateOpen(false)}
      clockGatePolicy={clockGatePolicy}
      clockGateDevices={clockGateDevices}
      openClockPreflight={openClockPreflight}
      snackbar={snackbar}
      closeSnackbar={closeSnackbar}
    />
  </Box>
);

export default PoliciesPageContent;
